import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadButton } from '@/features/upload/UploadButton';
import { UploadDropZone } from '@/features/upload/UploadDropZone';
import { useDatasetStore, effectiveDashboardName } from '@/store/dataset-store';
import { useFiltersStore } from '@/store/filters-store';
import { useWidgetsStore } from '@/store/widgets-store';
import { useUiStore } from '@/store/ui-store';
import { FilterBar } from '@/features/filters/FilterBar';
import { ManagePinnedFiltersForm } from '@/features/filters/pinned/ManagePinnedFiltersForm';
import { usePinnedFiltersStore } from '@/store/pinned-filters-store';
import { ColumnSettingsButton } from '@/features/column-settings/ColumnSettingsButton';
import { ColumnMatchCheckButton } from '@/features/column-settings/ColumnMatchCheckButton';
import { ValueGroupingsButton } from '@/features/value-groupings/ValueGroupingsButton';
import { SaveLoadButtons } from '@/features/save-load/SaveLoadButtons';
import { ExcelExportButton } from '@/features/export/ExcelExportButton';
import { DashboardPdfButton } from '@/features/export/DashboardPdfButton';
import { DashboardGrid } from '@/features/dashboard/DashboardGrid';
import { QueryBuilderForm } from '@/features/query-builder/QueryBuilderForm';
import { WidgetSettingsRightPanel } from '@/features/query-builder/WidgetSettingsRightPanel';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { PrivacyModal } from '@/components/PrivacyModal';
import { Sidebar } from '@/components/Sidebar';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useModeStore } from '@/store/mode-store';
import { ModeToggle } from '@/components/ModeToggle';
import { CompareBanner } from '@/features/compare/CompareBanner';
import { DataTable } from '@/widgets/DataTable';
import { DashboardNameInput } from './DashboardNameInput';
import { useDashboardLifecycle } from './use-dashboard-lifecycle';
import type { QuerySpec } from '@/types/query';
import type { WidgetConfig } from '@/types/widget';

/**
 * Dashboard shell: collapsible sidebar for management tools,
 * main area with filter bar + widget grid + data table.
 */
export function App(): JSX.Element {
  const { t, i18n } = useTranslation();
  const status = useDatasetStore((s) => s.status);
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const filters = useFiltersStore((s) => s.filters);
  const activeEntry = activeId ? datasets.get(activeId) : undefined;

  const widgets = useWidgetsStore((s) => s.widgets);
  const addWidget = useWidgetsStore((s) => s.addWidget);
  const removeWidget = useWidgetsStore((s) => s.removeWidget);
  const mode = useModeStore((s) => s.mode);
  const pinnedCols = usePinnedFiltersStore((s) => s.columns);

  const setActiveNav = useUiStore((s) => s.setActiveNav);
  const widgetSettingsTarget = useUiStore((s) => s.widgetSettingsTarget);
  const setWidgetSettingsTarget = useUiStore((s) => s.setWidgetSettingsTarget);
  const updateWidget = useWidgetsStore((s) => s.updateWidget);
  const settingsWidget = widgetSettingsTarget
    ? widgets.find((w) => w.id === widgetSettingsTarget)
    : undefined;

  // Widget loading + valueGroupings rehydration effects.
  useDashboardLifecycle();

  // Static table preview (not user-managed in Step 5).
  const tableSpec = useMemo<QuerySpec | null>(() => {
    if (!activeEntry) return null;
    // Pass transforms for filtered columns so filter values (mapped labels
    // like "İç Anadolu") match rows whose raw value is one of the sources.
    const groupings = activeEntry.valueGroupings;
    let transforms: QuerySpec['transforms'];
    if (groupings) {
      for (const f of filters) {
        const t = groupings[f.column];
        if (t) {
          transforms ??= {};
          transforms[f.column] = t;
        }
      }
    }
    const spec: QuerySpec = {
      source: activeEntry.id,
      filters,
      groupBy: [],
      metrics: [],
      limit: 500,
    };
    if (transforms) spec.transforms = transforms;
    return spec;
  }, [activeEntry, filters]);

  const handleRemoveWidget = (id: string): void => {
    void removeWidget(id);
  };

  const handleAddWidget = (cfg: WidgetConfig): void => {
    void addWidget(cfg);
  };

  const dataSubPanel = (
    <div className="flex flex-col gap-2">
      <UploadButton />
      {status.kind === 'loading' && (
        <div className="flex items-center gap-2 px-1">
          <div className="spinner" aria-hidden />
          <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {t('upload.loadingFile', { fileName: status.fileName })}
          </span>
        </div>
      )}
      {status.kind === 'error' && (
        <span
          className="text-xs px-1"
          style={{ color: 'var(--color-danger)' }}
          role="alert"
        >
          {status.message}
        </span>
      )}
      {activeEntry && (
        <div
          className="flex items-center gap-2 rounded-md mt-1"
          style={{
            padding: '7px 8px',
            background: 'var(--color-hover)',
            border: '1px solid var(--color-border)',
          }}
        >
          <span
            className="shrink-0 rounded-full"
            style={{
              width: 7,
              height: 7,
              background: 'var(--color-accent)',
            }}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div
              className="text-xs truncate"
              style={{ color: 'var(--color-fg)' }}
              title={effectiveDashboardName(activeEntry)}
            >
              {effectiveDashboardName(activeEntry)}
            </div>
            <div
              className="truncate"
              style={{ fontSize: '10px', color: 'var(--color-muted)' }}
            >
              {t('sidebar.data.rowCountLine', {
                rows: activeEntry.rowCount.toLocaleString(i18n.language),
                cols: activeEntry.columns.length,
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const widgetSubPanel = activeEntry ? (
    <QueryBuilderForm
      datasetId={activeEntry.id}
      columns={activeEntry.columns}
      onAdd={handleAddWidget}
      onClose={() => setActiveNav(null)}
    />
  ) : null;

  const filterSubPanel = activeEntry ? (
    <ManagePinnedFiltersForm
      columns={activeEntry.columns}
      pinned={pinnedCols}
      onClose={() => setActiveNav(null)}
    />
  ) : null;

  const dataconfSubPanel = activeEntry ? (
    <div className="flex flex-col gap-2">
      <ColumnSettingsButton />
      <ValueGroupingsButton />
    </div>
  ) : null;

  const dashboardSubPanel = activeEntry ? (
    <div className="flex flex-col gap-2">
      <SaveLoadButtons />
      <ExcelExportButton />
      <DashboardPdfButton />
      <ColumnMatchCheckButton />
    </div>
  ) : null;

  const appearanceSubPanel = (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <span
          className="text-[10px] font-bold uppercase tracking-wide"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('theme.label')}
        </span>
        <ThemeSwitcher />
      </div>
      <div className="flex flex-col gap-1.5">
        <span
          className="text-[10px] font-bold uppercase tracking-wide"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('language.label')}
        </span>
        <LanguageSwitcher />
      </div>
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Skip-to-content link — visible only on keyboard focus, lets
          screen-reader / keyboard users jump past the sidebar. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[300] focus:px-3 focus:py-1.5 focus:rounded focus:shadow-lg"
        style={{
          background: 'var(--color-accent)',
          color: '#fff',
        }}
      >
        {t('common.skipToContent')}
      </a>

      {/* Sidebar rail + sub-panel */}
      <Sidebar
        subPanels={{
          data: dataSubPanel,
          widget: widgetSubPanel,
          filter: filterSubPanel,
          dataconf: dataconfSubPanel,
          dashboard: dashboardSubPanel,
          appearance: appearanceSubPanel,
        }}
      />

      {/* Main content area */}
      <main
        id="main-content"
        tabIndex={-1}
        className="flex-1 min-w-0 flex flex-col overflow-hidden"
      >
        {/* Top bar — dashboard name + mode toggle */}
        <div
          className="flex items-center gap-2.5 px-3.5 shrink-0"
          style={{
            height: 'var(--top-bar-height)',
            background: 'var(--color-surface)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span
              className="text-xs font-bold uppercase whitespace-nowrap"
              style={{ color: 'var(--color-muted)', letterSpacing: '0.06em' }}
            >
              {t('topBar.dashboard')}:
            </span>
            {activeEntry && <DashboardNameInput entry={activeEntry} />}
          </div>
          {activeEntry && <ModeToggle />}
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 sm:px-4 lg:px-5">
          {mode === 'compare' && activeEntry && <CompareBanner />}

          {/* Widget dashboard */}
          {activeEntry && tableSpec ? (
            <div className="flex flex-col gap-3">
              <FilterBar />
              <DashboardGrid
                datasetId={activeId}
                widgets={widgets}
                filters={filters}
                onRemove={handleRemoveWidget}
                footer={
                  <div className="w-full">
                    <DataTable title={t('dataTable.previewTitle')} spec={tableSpec} />
                  </div>
                }
              />
            </div>
          ) : (
            // Empty state — render the drop zone so the landing surface
            // itself accepts files (Phase 10 step 11). The sidebar Upload
            // button stays as the secondary path.
            <UploadDropZone />
          )}
        </div>
      </main>

      {/* Widget settings — right panel (mutually exclusive with sub-panel) */}
      {settingsWidget && (
        <WidgetSettingsRightPanel
          widget={settingsWidget}
          onSave={(patch) => {
            void updateWidget(settingsWidget.id, patch);
          }}
          onClose={() => setWidgetSettingsTarget(null)}
        />
      )}

      {/* Privacy detail modal */}
      <PrivacyModal />
    </div>
  );
}
